import { useState, useEffect } from 'react';

import {
  Box,
  Flex,
  VStack,
  Text,
  Input,
  Textarea,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  SimpleGrid,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, DeleteIcon } from '@chakra-ui/icons';

import Const from '../Constants';
import { getAvatarUrl } from '../Utils';
import AgentsBar from '../Components/AgentsBar';
import ConfirmDeleteDialog from '../Components/ConfirmDeleteDialog';
import { useAgentsStore } from '../Stores/AgentsStore';

const kAvailableModels = [
  { id: 'claude-opus-4-7',           name: 'Claude Opus 4.7',   description: 'Most capable. Best for complex reasoning. Slower and more expensive.' },
  { id: 'claude-opus-4-6',           name: 'Claude Opus 4.6',   description: 'Previous Opus generation. Use if pinned for consistency.' },
  { id: 'claude-sonnet-4-6',         name: 'Claude Sonnet 4.6', description: 'Balanced quality and speed. Good default for most tasks.' },
  { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5',  description: 'Fastest and cheapest. Best for simple, high-volume tasks.' },
];

// ---- Agent Profile Editor (center panel) ----

const AgentEditor = ({ auth }) => {
  const currentAgent = useAgentsStore((state) => state.currentAgent);
  const updateAgent = useAgentsStore((state) => state.updateAgent);
  const addAgent = useAgentsStore((state) => state.addAgent);
  const deleteAgent = useAgentsStore((state) => state.deleteAgent);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleConfirmDelete = async () => {
    const id = currentAgent?.id;
    onClose();
    if (id) await deleteAgent(auth, id);
  };

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [briefing, setBriefing] = useState('');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [avatar, setAvatar] = useState(null);

  useEffect(() => {
    if (currentAgent) {
      setName(currentAgent.name || '');
      setRole(currentAgent.role || '');
      setBriefing(currentAgent.briefing || '');
      setModel(currentAgent.model || 'claude-sonnet-4-6');
      setAvatar(currentAgent.avatar || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAgent?.id]);

  if (!currentAgent) {
    return (
      <Flex flex={1} alignItems={'center'} justifyContent={'center'}>
        <Text color={Const.Gray165} fontSize={Const.SM}>
          Select or create an agent to get started.
        </Text>
      </Flex>
    );
  }

  const isReadOnly = currentAgent.type === Const.sGlobal;

  const handleNameBlur = () => {
    if (name !== currentAgent.name) {
      updateAgent(auth, currentAgent.id, name, null, null, null, undefined);
    }
  };

  const handleRoleBlur = () => {
    if (role !== currentAgent.role) {
      updateAgent(auth, currentAgent.id, null, role, null, null, undefined);
    }
  };

  const handleBriefingBlur = () => {
    if (briefing !== currentAgent.briefing) {
      updateAgent(auth, currentAgent.id, null, null, briefing, null, undefined);
    }
  };

  const handleModelChange = (e) => {
    const newModel = e.target.value;
    setModel(newModel);
    updateAgent(auth, currentAgent.id, null, null, null, newModel, undefined);
  };

  const handleAvatarSelect = (src) => {
    setAvatar(src);
    updateAgent(auth, currentAgent.id, null, null, null, null, src);
  };

  const handleSaveCopy = () => {
    addAgent(auth, currentAgent.id);
  };

  const avatarNode = (
    <Avatar
      src={getAvatarUrl(avatar)}
      name={name || 'Agent'}
      size={'xl'}
      background={Const.Gray64}
      color={Const.White}
      borderWidth={2}
    />
  );

  return (
    <VStack flex={1} alignItems={'stretch'} p={Const.SpaceLG} spacing={4} overflowY={'auto'}>
      <Flex justifyContent={'flex-end'}>
        {isReadOnly ? (
          <Button
            size={Const.SM}
            height={'30px'}
            background={Const.DoneColor}
            color={Const.White}
            _hover={{ background: Const.DoneLightColor }}
            fontSize={Const.XS}
            onClick={handleSaveCopy}
          >
            Clone
          </Button>
        ) : (
          <Button
            leftIcon={<DeleteIcon />}
            iconSpacing={0}
            size={Const.SM}
            height={'30px'}
            background={Const.Gray69}
            color={Const.Gray165}
            _hover={{ background: Const.Red600 }}
            fontSize={Const.XS}
            onClick={onOpen}
          />
        )}
      </Flex>
      <Flex justifyContent={'center'} pb={Const.SpaceSM}>
        {isReadOnly ? (
          avatarNode
        ) : (
          <Popover placement={'bottom'}>
            <PopoverTrigger>
              <Box cursor={'pointer'} position={'relative'} _hover={{ opacity: 0.85 }}>
                {avatarNode}
                <Box
                  position={'absolute'} bottom={0} right={0}
                  background={Const.Gray46} borderRadius={'full'}
                  borderWidth={1} borderColor={Const.Gray64}
                  px={Const.SpaceXS} py={'1px'}
                >
                  <Text fontSize={Const.XS} color={Const.Gray165}>Edit</Text>
                </Box>
              </Box>
            </PopoverTrigger>
            <PopoverContent background={Const.Gray46} borderColor={Const.Gray64} w={'260px'}>
              <PopoverBody>
                <SimpleGrid columns={4} spacing={Const.SpaceMD} justifyItems={'center'} margin={1}>
                  {Const.kAgentAvatars.map((src) => (
                    <Avatar
                      key={src}
                      src={getAvatarUrl(src)}
                      name={'Agent'}
                      size={Const.MD}
                      borderWidth={2}
                      borderColor={avatar === src ? Const.Orange400 : Const.White}
                      cursor={'pointer'}
                      onClick={() => handleAvatarSelect(src)}
                      _hover={{ opacity: 0.8 }}
                    />
                  ))}
                </SimpleGrid>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Flex>
      <Box>
        <Text color={Const.Gray165} fontSize={Const.SM} mb={Const.SpaceXS} textAlign={Const.Left} fontWeight={Const.Bold}>Agent Name</Text>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          isReadOnly={isReadOnly}
          placeholder={'Agent name'}
          fontSize={Const.MD}
          fontWeight={Const.Bold}
          color={Const.White}
          background={Const.Gray46}
          borderColor={Const.Gray64}
          _hover={{ borderColor: isReadOnly ? Const.Gray64 : Const.Gray100 }}
          _focus={{ borderColor: isReadOnly ? Const.Gray64 : Const.Orange400, boxShadow: 'none' }}
        />
      </Box>

      <Box>
        <Text color={Const.Gray165} fontSize={Const.SM} mb={Const.SpaceXS} textAlign={Const.Left} fontWeight={Const.Bold}>Role</Text>
        <Input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          onBlur={handleRoleBlur}
          isReadOnly={isReadOnly}
          placeholder={'e.g. Technical recruiting specialist'}
          fontSize={Const.MD}
          color={Const.White}
          background={Const.Gray46}
          borderColor={Const.Gray64}
          _hover={{ borderColor: isReadOnly ? Const.Gray64 : Const.Gray100 }}
          _focus={{ borderColor: isReadOnly ? Const.Gray64 : Const.Orange400, boxShadow: 'none' }}
        />
      </Box>

      <Box flex={1} display={'flex'} flexDirection={'column'}>
        <Text color={Const.Gray165} fontSize={Const.SM} mb={Const.SpaceXS} textAlign={Const.Left} fontWeight={Const.Bold}>Briefing (system prompt)</Text>
        <Textarea
          flex={1}
          value={briefing}
          onChange={(e) => setBriefing(e.target.value)}
          onBlur={handleBriefingBlur}
          isReadOnly={isReadOnly}
          placeholder={'Describe this agent\'s expertise, tone, and standing instructions...'}
          fontSize={Const.MD}
          color={Const.White}
          background={Const.Gray46}
          borderColor={Const.Gray64}
          _hover={{ borderColor: isReadOnly ? Const.Gray64 : Const.Gray100 }}
          _focus={{ borderColor: isReadOnly ? Const.Gray64 : Const.Orange400, boxShadow: 'none' }}
          resize={'none'}
        />
      </Box>

      <Box>
        <Text color={Const.Gray165} fontSize={Const.SM} mb={Const.SpaceXS} textAlign={Const.Left} fontWeight={Const.Bold}>Model</Text>
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={isReadOnly ? null : <ChevronDownIcon />}
            isDisabled={isReadOnly}
            width={'100%'}
            bg={Const.Gray46}
            color={Const.White}
            border={'1px solid'}
            borderColor={Const.Gray64}
            borderRadius={'md'}
            fontSize={Const.SM}
            textAlign={'left'}
            fontWeight={'normal'}
            _hover={{ borderColor: isReadOnly ? Const.Gray64 : Const.Gray100, bg: Const.Gray46 }}
            _focus={{ boxShadow: 'none' }}
            _expanded={{ bg: Const.Gray46 }}
            _disabled={{ opacity: 1, cursor: 'default' }}
          >
            {kAvailableModels.find((m) => m.id === model)?.name || model}
          </MenuButton>
          <MenuList bg={Const.Gray36} borderColor={Const.Gray64} borderRadius={'md'}>
            {kAvailableModels.map((m) => (
              <MenuItem
                key={m.id}
                bg={Const.Gray36}
                color={Const.White}
                fontSize={Const.SM}
                _hover={{ bg: Const.Gray64 }}
                onClick={() => handleModelChange({ target: { value: m.id } })}
              >
                <VStack spacing={0} align={'start'}>
                  <Text fontSize={Const.SM}>{m.name}</Text>
                  <Text fontSize={'xs'} color={Const.Gray165}>{m.description}</Text>
                </VStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Box>

      <ConfirmDeleteDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        title={'Delete agent'}
        body={`Are you sure you want to delete${currentAgent?.name ? ` "${currentAgent.name}"` : ' this agent'}? This can't be undone.`}
      />
    </VStack>
  );
};

// ---- Main Agents View ----

const Agents = ({ auth }) => {
  const loadAgents = useAgentsStore((state) => state.loadAgents);

  useEffect(() => {
    loadAgents(auth);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex height={'100vh'} width={'100%'} overflow={'hidden'}>
      <AgentsBar auth={auth} />
      <AgentEditor auth={auth} />
    </Flex>
  );
};

export default Agents;
